import type { SerializedError } from '@reduxjs/toolkit';
import type {
  AIGeneratedSong,
  CustomizableSong,
  EnergyType,
  FreeSong,
  IGenre,
} from 'services/api/entities';
import type { IBaseSong } from 'services/api/entities/song/interfaces';
import { UserLoop as UserLoopValue } from 'types/userLoop';

export type RemixerSong = AIGeneratedSong | FreeSong | CustomizableSong;

export type HistorySong = Pick<IBaseSong, 'id' | 'musicFilePath' | 'duration'>;

export enum FormulaFields {
  Genre = 'genre',
  Energy = 'energy',
  Bpm = 'bpm',
  Duration = 'duration',
}

export enum ExtraFormulaFields {
  UserLoop = 'user_loop',
  RemoveUserLoop = 'remove_user_loop',
}

export interface Genre {
  field: FormulaFields.Genre;
  value: IGenre['name'];
}
export interface Bpm {
  field: FormulaFields.Bpm;
  value: number;
}
export interface Energy {
  field: FormulaFields.Energy;
  value: EnergyType;
}
export interface Duration {
  field: FormulaFields.Duration;
  value: number;
}
export interface UserLoop {
  field: ExtraFormulaFields;
  value: UserLoopValue;
}

export type FormulaField = Duration | Bpm | Genre | Energy;
export type ExtendedFormulaField = FormulaField | UserLoop;
export type FormulaFieldType = FormulaField['field'];
export type FormulaFieldValue = FormulaField['value'];

interface SongLoading {
  loading: true;
  error: null;
  data: null;
}

interface SongSuccess {
  loading: false;
  error: null;
  data: RemixerSong | HistorySong;
}

interface SongError {
  loading: false;
  error: SerializedError;
  data: null;
}

export type RemixerSongState = SongLoading | SongSuccess | SongError;

export interface RemixerHistoryItem {
  songRequestId: string;
  formula: ExtendedFormulaField;
}

export enum Effects {
  radio = 'radio',
  destroyer = 'destroyer',
  heavenLow = 'heavenLow',
  spaciousness = 'spaciousness',
  master = 'master',
}

export type EffectValues = Record<Effects, number>;

export type ProjectSong = IBaseSong | null;

export interface RemixerProject {
  /**
   * A property representing current studio project id (issued on the backend).
   */
  id: string | null;

  createdAt: string | null;
  lastSavedAt: number | null;
  title: string | null;
  musicFilePath: string | null;
  isInLibrary: boolean;
  duration: number | null;

  song?: ProjectSong;
  vegaSong?: ProjectSong;

  /**
   * A property representing effects state.
   */
  effects: EffectValues;

  /**
   * A property representing chronological order of requested songs and their remix parameters.
   *  contains reference to *song* item
   */
  history: Array<RemixerHistoryItem>;

  /**
   * A property representing current history pointer (what changes are applied at the moment).
   */
  historyPointer: number;

  /**
   * A property representing effects order state
   */
  effectsOrder: Array<Effects>;
}

export interface RemixerState {
  /**
   * A property representing current project state.
   */
  project: RemixerProject;

  /**
   * A property representing song states record.
   */
  songs: Record<string, RemixerSongState>;

  /**
   * A property representing **initial song** Request Id.
   */
  initialSongRequestId: string | null;

  /**
   * A property representing reference project state to compare current state to.
   */
  prevProjectState: RemixerProject | null;
}
